"use client";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { motion, useReducedMotion } from "framer-motion";
import _ from "lodash";

import CarCard from "@components/ui/car-card";
import Filters from "@modules/car-list/components/filter";
import { CarData } from "@src/types/car";
import { facilities } from "@/data/facilities";

const DEFAULT_TAKE = 12;
const LOAD_MORE_COUNT = 6;

const CarList = ({ data, searchParams }: { data: CarData[]; searchParams: any }) => {
	const sp = useSearchParams();
	const { replace } = useRouter();
	const pathname = usePathname();
	const shouldReduceMotion = useReducedMotion();

	const [filters, setFilters] = useState<any>(searchParams);
	const [cars, setCars] = useState<CarData[]>([...data]);

	const initialTake = searchParams?.take ? parseInt(searchParams.take, 10) : DEFAULT_TAKE;
	const [take, setTake] = useState(initialTake);
	const [initialized, setInitialized] = useState(false);

	const filteredData = (car: CarData) => {
		return Object.keys(filters).every((filter: any) => {
			if (filter === "take") return true; // skip pagination param
			if (filter === "body") {
				if (filters[filter]) return car.bodyType === filters[filter];
				else return true;
			}
			if (filter === "make") {
				if (filters[filter]) return car.make === filters[filter];
				else return true;
			}
			if (filter === "type") {
				if (filters[filter]) return car.vehicleType === filters[filter];
				else return true;
			}
			if (filter === "search") {
				if (filters[filter]) {
					const expr = new RegExp(`.?(${filters.search.replace(" ", "|")})`, "gim");
					return expr.test(car.name);
				} else return true;
			}
			if (filter === "facility") {
				if (filters[filter]) return car.facilitySlug === filters[filter];
				else return true;
			}

			return true;
		});
	};

	useEffect(() => {
		if (filters) setCars(data.filter(filteredData));
	}, [filters]);

	const handleFilterSubmit = (f: any) => {
		setFilters(f);
		// Only reset take on user-triggered filter changes, not initial mount
		if (initialized) {
			setTake(DEFAULT_TAKE);
		} else {
			setInitialized(true);
		}
	};

	const visibleCars = useMemo(() => cars.slice(0, take), [cars, take]);
	const hasMore = take < cars.length;

	// Body-type and make options depend on the selected vehicle type, so picking
	// "Bilar" hides motorcycle-only body types/makes (and vice versa).
	const optionSource = useMemo(
		() => (filters?.type ? data.filter((d) => d.vehicleType === filters.type) : data),
		[data, filters?.type]
	);

	const loadMore = useCallback(() => {
		const newTake = Math.min(take + LOAD_MORE_COUNT, cars.length);
		setTake(newTake);

		// Keep the take param in the URL so the view is shareable/restorable.
		const params = new URLSearchParams(sp);
		params.set("take", String(newTake));
		replace(`${pathname}?${params.toString()}`, { scroll: false });
	}, [take, cars.length, sp, pathname, replace]);

	// Auto-load the next page when the sentinel scrolls into view (infinite scroll).
	// Re-runs after each load (loadMore changes with `take`) so it keeps filling
	// until the sentinel leaves the viewport or there's nothing left.
	const sentinelRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		const el = sentinelRef.current;
		if (!el || !hasMore) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) loadMore();
			},
			{ rootMargin: "300px 0px" }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasMore, loadMore]);

	return (
		<>
			<Filters
				count={cars?.length}
				searchParams={searchParams}
				onSubmit={handleFilterSubmit}
				bodyTypes={_.uniq(optionSource?.map((d) => d.bodyType).filter(Boolean).sort())}
				make={_.uniq(optionSource?.map((d) => d.make).filter(Boolean).sort())}
				vehicleTypes={_.uniq(data?.map((d) => d.vehicleType).filter(Boolean).sort())}
				facilities={facilities.filter((f) => f.status === "active").map((f) => ({ slug: f.slug, name: f.name }))}
			/>

			{cars?.length ? (
				<>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
						{visibleCars.map((car, i) => (
							<motion.div
								className="h-full"
								key={car.id}
								initial={shouldReduceMotion ? false : { opacity: 0, y: 24 }}
								whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
								viewport={{ once: true, margin: "-10%" }}
								transition={{ duration: 0.45, delay: (i % 3) * 0.06, ease: [0.17, 0.55, 0.55, 1] }}
							>
								<CarCard car={car} />
							</motion.div>
						))}
					</div>

					{/* Progress + infinite-scroll sentinel */}
					{hasMore && (
						<div className="flex flex-col items-center mt-12 gap-4">
							{/* Progress bar */}
							<div className="flex items-center gap-3 text-sm text-gray-500">
								<span>
									Visar {visibleCars.length} av {cars.length}
								</span>
							</div>
							<div className="w-48 h-[2px] bg-surface-hover rounded-full overflow-hidden">
								<div
									className="h-full bg-accent transition-all duration-500 ease-out rounded-full"
									style={{ width: `${(visibleCars.length / cars.length) * 100}%` }}
								/>
							</div>

							{/* Sentinel: triggers the next page when scrolled near */}
							<div ref={sentinelRef} className="flex items-center gap-2 text-sm text-gray-500 py-2">
								<span className="inline-block w-4 h-4 border-2 border-white/20 border-t-accent rounded-full animate-spin" />
								Laddar fler…
							</div>
						</div>
					)}
				</>
			) : (
				<div className="text-center py-16">
					<p className="text-gray-400 text-lg">Inga fordon hittades</p>
					<p className="text-gray-600 text-sm mt-1">Prova att ändra dina filter</p>
				</div>
			)}
		</>
	);
};

export default CarList;
